import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import {
  Save,
  Security,
  Notifications,
  Business,
  Settings as SettingsIcon,
} from '@mui/icons-material';

interface AdminSettings {
  general: {
    platformName: string;
    platformDescription: string;
    contactEmail: string;
    contactPhone: string;
    address: string;
    timezone: string;
    currency: string;
  };
  security: {
    requireEmailVerification: boolean;
    requirePhoneVerification: boolean;
    allowVendorSelfRegistration: boolean;
    requireVendorApproval: boolean;
    sessionTimeout: number;
    maxLoginAttempts: number;
  };
  notifications: {
    emailNotifications: boolean;
    smsNotifications: boolean;
    pushNotifications: boolean;
    adminEmailAlerts: boolean;
    vendorApprovalAlerts: boolean;
    offerApprovalAlerts: boolean;
  };
  features: {
    enableReviews: boolean;
    enableRatings: boolean;
    enableFavorites: boolean;
    enableLocationServices: boolean;
    enablePushNotifications: boolean;
    enableAnalytics: boolean;
  };
}

const Settings: React.FC = () => {
  const [settings, setSettings] = useState<AdminSettings>({
    general: {
      platformName: 'DiscountDays',
      platformDescription: 'Your gateway to amazing local deals and discounts',
      contactEmail: 'info@godiscountdays.com',
      contactPhone: '+1 (555) 123-4567',
      address: '123 Business St, City, State 12345',
      timezone: 'America/New_York',
      currency: 'USD',
    },
    security: {
      requireEmailVerification: true,
      requirePhoneVerification: false,
      allowVendorSelfRegistration: true,
      requireVendorApproval: true,
      sessionTimeout: 30,
      maxLoginAttempts: 5,
    },
    notifications: {
      emailNotifications: true,
      smsNotifications: false,
      pushNotifications: true,
      adminEmailAlerts: true,
      vendorApprovalAlerts: true,
      offerApprovalAlerts: true,
    },
    features: {
      enableReviews: true,
      enableRatings: true,
      enableFavorites: true,
      enableLocationServices: true,
      enablePushNotifications: true,
      enableAnalytics: true,
    },
  });

  const [isLoading, setIsLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' });
  const [activeTab, setActiveTab] = useState('general');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_ADMIN_URL || 'https://godiscountdays.com/api/admin'}/settings`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      
      if (data.success && data.data) {
        // Update settings with API data
        setSettings({
          general: {
            platformName: data.data.general?.platformName || 'DiscountDays',
            platformDescription: data.data.general?.platformDescription || 'Your gateway to amazing local deals and discounts',
            contactEmail: data.data.general?.contactEmail || 'info@godiscountdays.com',
            contactPhone: data.data.general?.contactPhone || '+1 (555) 123-4567',
            address: data.data.general?.address || '123 Business St, City, State 12345',
            timezone: data.data.general?.timezone || 'America/New_York',
            currency: data.data.general?.currency || 'USD',
          },
          security: {
            requireEmailVerification: data.data.security?.requireEmailVerification ?? true,
            requirePhoneVerification: data.data.security?.requirePhoneVerification ?? false,
            allowVendorSelfRegistration: data.data.security?.allowVendorSelfRegistration ?? true,
            requireVendorApproval: data.data.security?.requireVendorApproval ?? true,
            sessionTimeout: data.data.security?.sessionTimeout || 30,
            maxLoginAttempts: data.data.security?.maxLoginAttempts || 5,
          },
          notifications: {
            emailNotifications: data.data.notifications?.emailNotifications ?? true,
            smsNotifications: data.data.notifications?.smsNotifications ?? false,
            pushNotifications: data.data.notifications?.pushNotifications ?? true,
            adminEmailAlerts: data.data.notifications?.adminEmailAlerts ?? true,
            vendorApprovalAlerts: data.data.notifications?.vendorApprovalAlerts ?? true,
            offerApprovalAlerts: data.data.notifications?.offerApprovalAlerts ?? true,
          },
          features: {
            enableReviews: data.data.features?.enableReviews ?? true,
            enableRatings: data.data.features?.enableRatings ?? true,
            enableFavorites: data.data.features?.enableFavorites ?? true,
            enableLocationServices: data.data.features?.enableLocationServices ?? true,
            enablePushNotifications: data.data.features?.enablePushNotifications ?? true,
            enableAnalytics: data.data.features?.enableAnalytics ?? true,
          },
        });
      } else {
        console.warn('Settings API returned unsuccessful response:', data);
        // Keep default settings if API fails
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
      // Keep default settings if API fails
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${process.env.REACT_APP_ADMIN_URL || 'https://godiscountdays.com/api/admin'}/settings`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(settings),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSnackbar({
          open: true,
          message: data.message || 'Settings saved successfully!',
          severity: 'success',
        });
      } else {
        throw new Error(data.message || 'Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving settings:', error);
      setSnackbar({
        open: true,
        message: 'Failed to save settings. Please try again.',
        severity: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSettingChange = (section: keyof AdminSettings, field: string, value: any) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const tabs = [
    { id: 'general', label: 'General', icon: <SettingsIcon /> },
    { id: 'security', label: 'Security', icon: <Security /> },
    { id: 'notifications', label: 'Notifications', icon: <Notifications /> },
    { id: 'features', label: 'Features', icon: <Business /> },
  ];

  if (isLoading && activeTab === 'general') {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <Typography>Loading settings...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Settings
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        Configure platform settings and preferences.
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
        {/* Settings Navigation */}
        <Box sx={{ flex: '1 1 250px', minWidth: '250px' }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Settings Categories
              </Typography>
              <List>
                {tabs.map((tab) => (
                  <ListItem
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    sx={{
                      borderRadius: 1,
                      mb: 0.5,
                      cursor: 'pointer',
                      backgroundColor: activeTab === tab.id ? 'primary.light' : 'transparent',
                      color: activeTab === tab.id ? 'primary.contrastText' : 'inherit',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <Box sx={{ mr: 2 }}>{tab.icon}</Box>
                    <ListItemText primary={tab.label} />
                  </ListItem>
                ))}
              </List>
            </CardContent>
          </Card>
        </Box>

        {/* Settings Content */}
        <Box sx={{ flex: '1 1 600px', minWidth: '600px' }}>
          {/* General Settings */}
          {activeTab === 'general' && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  General Settings
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                    <TextField
                      fullWidth
                      label="Platform Name"
                      value={settings.general.platformName}
                      onChange={(e) => handleSettingChange('general', 'platformName', e.target.value)}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                    <TextField
                      fullWidth
                      label="Contact Email"
                      value={settings.general.contactEmail}
                      onChange={(e) => handleSettingChange('general', 'contactEmail', e.target.value)}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 100%', minWidth: '100%' }}>
                    <TextField
                      fullWidth
                      label="Platform Description"
                      value={settings.general.platformDescription}
                      onChange={(e) => handleSettingChange('general', 'platformDescription', e.target.value)}
                      multiline
                      rows={3}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                    <TextField
                      fullWidth
                      label="Contact Phone"
                      value={settings.general.contactPhone}
                      onChange={(e) => handleSettingChange('general', 'contactPhone', e.target.value)}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                    <FormControl fullWidth>
                      <InputLabel>Currency</InputLabel>
                      <Select
                        value={settings.general.currency}
                        label="Currency"
                        onChange={(e) => handleSettingChange('general', 'currency', e.target.value)}
                      >
                        <MenuItem value="USD">USD</MenuItem>
                        <MenuItem value="EUR">EUR</MenuItem>
                        <MenuItem value="GBP">GBP</MenuItem>
                        <MenuItem value="CAD">CAD</MenuItem>
                      </Select>
                    </FormControl>
                  </Box>
                  <Box sx={{ flex: '1 1 100%', minWidth: '100%' }}>
                    <TextField
                      fullWidth
                      label="Address"
                      value={settings.general.address}
                      onChange={(e) => handleSettingChange('general', 'address', e.target.value)}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Security Settings */}
          {activeTab === 'security' && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Security Settings
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  <Box sx={{ flex: '1 1 100%', minWidth: '100%' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.security.requireEmailVerification}
                          onChange={(e) => handleSettingChange('security', 'requireEmailVerification', e.target.checked)}
                        />
                      }
                      label="Require Email Verification"
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 100%', minWidth: '100%' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.security.requirePhoneVerification}
                          onChange={(e) => handleSettingChange('security', 'requirePhoneVerification', e.target.checked)}
                        />
                      }
                      label="Require Phone Verification"
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 100%', minWidth: '100%' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.security.allowVendorSelfRegistration}
                          onChange={(e) => handleSettingChange('security', 'allowVendorSelfRegistration', e.target.checked)}
                        />
                      }
                      label="Allow Vendor Self Registration"
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 100%', minWidth: '100%' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.security.requireVendorApproval}
                          onChange={(e) => handleSettingChange('security', 'requireVendorApproval', e.target.checked)}
                        />
                      }
                      label="Require Vendor Approval"
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                    <TextField
                      fullWidth
                      label="Session Timeout (minutes)"
                      type="number"
                      value={settings.security.sessionTimeout}
                      onChange={(e) => handleSettingChange('security', 'sessionTimeout', parseInt(e.target.value))}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 300px', minWidth: '300px' }}>
                    <TextField
                      fullWidth
                      label="Max Login Attempts"
                      type="number"
                      value={settings.security.maxLoginAttempts}
                      onChange={(e) => handleSettingChange('security', 'maxLoginAttempts', parseInt(e.target.value))}
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Notification Settings */}
          {activeTab === 'notifications' && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Notification Settings
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  <Box sx={{ flex: '1 1 100%', minWidth: '100%' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.notifications.emailNotifications}
                          onChange={(e) => handleSettingChange('notifications', 'emailNotifications', e.target.checked)}
                        />
                      }
                      label="Email Notifications"
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 100%', minWidth: '100%' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.notifications.smsNotifications}
                          onChange={(e) => handleSettingChange('notifications', 'smsNotifications', e.target.checked)}
                        />
                      }
                      label="SMS Notifications"
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 100%', minWidth: '100%' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.notifications.pushNotifications}
                          onChange={(e) => handleSettingChange('notifications', 'pushNotifications', e.target.checked)}
                        />
                      }
                      label="Push Notifications"
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 100%', minWidth: '100%' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.notifications.adminEmailAlerts}
                          onChange={(e) => handleSettingChange('notifications', 'adminEmailAlerts', e.target.checked)}
                        />
                      }
                      label="Admin Email Alerts"
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 100%', minWidth: '100%' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.notifications.vendorApprovalAlerts}
                          onChange={(e) => handleSettingChange('notifications', 'vendorApprovalAlerts', e.target.checked)}
                        />
                      }
                      label="Vendor Approval Alerts"
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 100%', minWidth: '100%' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.notifications.offerApprovalAlerts}
                          onChange={(e) => handleSettingChange('notifications', 'offerApprovalAlerts', e.target.checked)}
                        />
                      }
                      label="Offer Approval Alerts"
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Feature Settings */}
          {activeTab === 'features' && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Feature Settings
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                  <Box sx={{ flex: '1 1 100%', minWidth: '100%' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.features.enableReviews}
                          onChange={(e) => handleSettingChange('features', 'enableReviews', e.target.checked)}
                        />
                      }
                      label="Enable Reviews"
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 100%', minWidth: '100%' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.features.enableRatings}
                          onChange={(e) => handleSettingChange('features', 'enableRatings', e.target.checked)}
                        />
                      }
                      label="Enable Ratings"
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 100%', minWidth: '100%' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.features.enableFavorites}
                          onChange={(e) => handleSettingChange('features', 'enableFavorites', e.target.checked)}
                        />
                      }
                      label="Enable Favorites"
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 100%', minWidth: '100%' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.features.enableLocationServices}
                          onChange={(e) => handleSettingChange('features', 'enableLocationServices', e.target.checked)}
                        />
                      }
                      label="Enable Location Services"
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 100%', minWidth: '100%' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.features.enablePushNotifications}
                          onChange={(e) => handleSettingChange('features', 'enablePushNotifications', e.target.checked)}
                        />
                      }
                      label="Enable Push Notifications"
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 100%', minWidth: '100%' }}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={settings.features.enableAnalytics}
                          onChange={(e) => handleSettingChange('features', 'enableAnalytics', e.target.checked)}
                        />
                      }
                      label="Enable Analytics"
                    />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Save Button */}
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              startIcon={<Save />}
              onClick={handleSave}
              disabled={isLoading}
              size="large"
            >
              {isLoading ? 'Saving...' : 'Save Settings'}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Settings;
